import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDown from '@material-ui/icons/KeyboardArrowDown';
import { setStep } from 'src/redux/app/actions';
import Label from 'components/_utility/Label';
import { getBikeModel, getFinanceOption, getLoadedConfiguration, getCalculations } from 'src/redux/app/selectors';
import usePrice from 'src/hooks/usePrice';
import { getCapCode } from '@triumph/shared';
import useDataLayer from 'src/hooks/useDataLayer';
import getTermsAndConditions from 'src/requests/codeweavers/terms-and-conditions';
import { getLanguageCode } from 'helpers/utilities';
import financeParameters from 'src/requests/finance/parameters';
import useCodeweaversApiKey from 'src/hooks/useCodeweaversApiKey';
import {
  Wrap,
  Title,
  Content,
  List,
  ListItem,
  ListItemLabel,
  ListItemValue,
  ButtonGroup,
  Button,
  ArrowRight,
  Summary,
  TitleAlt,
} from './YourFinanceQuote.styled';

const YourFinanceQuote = ({ showQuote, onSetShowQuote, isSummaryPage = false }) => {
  const dispatch = useDispatch();
  const pushToDataLayer = useDataLayer();
  const codeweaversKey = useCodeweaversApiKey();
  const [showAccordionContent, setShowAccordionContent] = useState(isSummaryPage);
  const [columns, setColumns] = useState([]);
  const [closingText, setClosingText] = useState();
  const selectedFinanceOption = useSelector(getFinanceOption);
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const bikeModel = useSelector(getBikeModel);
  const calculations = useSelector(getCalculations);
  const { colours } = bikeModel;
  const capCode = getCapCode(colours, loadedConfiguration);
  const formatPrice = usePrice();

  useEffect(() => {
    if (typeof window.codeweavers !== 'undefined' && capCode && calculations) {
      const createCodeweaversColumns = async () => {
        const parameters = (await financeParameters()) || [];

        const {
          blocks: [calculation],
        } = calculations;
        const newColumns = calculation.details
          .map(({ label, displayValue: value, key }) => {
            const parameter = parameters.find(
              ({ Key: parameterKey }) => parameterKey.toLowerCase() === key.toLowerCase()
            );

            const shouldExcludeFromHighlighting =
              parameter?.ExcludeFrom?.includes?.(selectedFinanceOption.financeProductName) || false;

            const shouldHighlight = parameter?.HighlightField || false;

            return {
              label,
              value,
              highlight: !shouldExcludeFromHighlighting && shouldHighlight,
              order:
                isNaN(parameter?.OrderId) || shouldExcludeFromHighlighting
                  ? Number.MAX_SAFE_INTEGER
                  : parameter?.OrderId,
            };
          })
          .sort((a, b) => a.order - b.order);

        setColumns(newColumns);
      };

      createCodeweaversColumns();
    }

    if (typeof window.scfSimulation === 'object' && capCode) {
      const createSantanderColumns = async () => {
        const data = await financeParameters();
        const newColumns = [];

        const simulationCheck = setInterval(() => {
          if (calculations) {
            Object.entries(calculations)
              .filter(([key]) => key !== 'simulationChecksum')
              .forEach(([key, value]) => {
                const currentParameter = data.find((parameter) => parameter.Key === key);
                let tableValueInnerText = value.value;

                if (!currentParameter) {
                  return;
                }

                if (currentParameter.Type === 'price') {
                  tableValueInnerText = formatPrice(value.value);
                }

                if (currentParameter.Type === 'interest') {
                  tableValueInnerText += '%';
                }

                if (currentParameter.Prefix) {
                  tableValueInnerText = `${currentParameter.Prefix} ${tableValueInnerText}`;
                }

                if (currentParameter.PostFix) {
                  tableValueInnerText += ` ${currentParameter.PostFix}`;
                }

                newColumns.push({
                  label: currentParameter.Label,
                  value: tableValueInnerText,
                  highlight: false,
                });
              });

            setColumns(newColumns);
            clearInterval(simulationCheck);
          }
        }, 500);
      };

      createSantanderColumns();
    }
  }, [calculations]);

  const quoteRef = localStorage.getItem('quoteRef');

  useEffect(() => {
    const isFrance = getLanguageCode() === 'fr-FR';

    if (!isFrance || !quoteRef) {
      setClosingText(selectedFinanceOption.closingText);
      return;
    }

    getTermsAndConditions(codeweaversKey(), quoteRef).then(terms =>
      setClosingText(
        terms
          .split('<br>')
          .map(part => `<p>${part}</p>`)
          .join('')
      )
    );
  }, [quoteRef]);

  return (
    showQuote &&
    !!columns?.length && (
      <Wrap>
        {isSummaryPage ? (
          <TitleAlt>
            <Label name="yourCurrentQuoteTitle"></Label>
          </TitleAlt>
        ) : (
          <Title
            onClick={() => {
              setShowAccordionContent(!showAccordionContent);
              pushToDataLayer(showAccordionContent ? 'purchaseQteClose' : 'purchaseQteOpen');
            }}
            isOpen={showAccordionContent}
          >
            <Label name="yourFinanceQuoteTitle"></Label>
            <ArrowDown />
          </Title>
        )}
        <Content isOpen={showAccordionContent} isSummaryPage={isSummaryPage}>
          <List>
            {columns.map(row => (
              <ListItem key={row.label}>
                <ListItemLabel $highlight={row.highlight}>{row.label}</ListItemLabel>
                <ListItemValue $highlight={row.highlight}>{row.value}</ListItemValue>
              </ListItem>
            ))}
          </List>
          {!isSummaryPage && (
            <ButtonGroup>
              <Button
                primary
                onClick={() => {
                  pushToDataLayer('purchaseQteRemove');
                  onSetShowQuote(false);
                }}
              >
                <Label name="financeRemoveQuoteLabel"></Label>
              </Button>
              <Button
                onClick={() => {
                  pushToDataLayer('purchaseQteEdit');
                  dispatch(setStep(0));
                }}
              >
                <Label name="financeEditQuoteLabel"></Label>
                <ArrowRight fontSize="small" />
              </Button>
            </ButtonGroup>
          )}
          {closingText && <Summary dangerouslySetInnerHTML={{ __html: closingText }} />}
        </Content>
      </Wrap>
    )
  );
};

export default YourFinanceQuote;
